import { replaceEqualDeep } from '@tanstack/react-query';
import { create } from 'zustand';

import type { EventEntity, MarketEntity, SeriesEntity } from 'queries/marketEntityTransform';
import { isLowMemoryDevice } from 'utils/device';

/**
 * Normalized, REST-owned mirror of the market/event data used across the app: identity, metadata,
 * relationships (`event.marketTickers`), and an LRU cap on retained events.
 *
 * This store holds NO WebSocket subscription. It used to own a `createTickerWatcher` over the
 * `ticker` and `market_lifecycle_v2` channels, which made every entity live — and meant a single
 * price tick replaced this store's `markets` record, invalidating every page-level selector that
 * reads an array of markets and cascading a re-render through each parent between the store and the
 * leaf that actually changed. It also duplicated `lib/stores/tickerQuotesStore.ts`, which carries
 * the same two channels, so each frame was parsed twice.
 *
 * Live data now belongs to whoever renders it: a market card subscribes its own ticker through
 * `hooks/useLiveMarketEntity` (or `useLiveEventMarkets` for aggregate consumers) and overlays the
 * quote + lifecycle status onto the entity locally, so a tick re-renders one leaf. Freshness of
 * everything WS doesn't carry — metadata, `previousPeriodPrice`, markets that open mid-session —
 * comes from REST polling; see `react_pages/Event/hooks/eventPolling.ts`.
 */

/**
 * Least-recently-accessed cap on `events`, excluding events retained by mounted consumers.
 * Doesn't yet protect tickers referenced by
 * open positions/resting orders — that cross-store guard is Phase 5 (portfolio
 * decoupling), once this store has a way to ask the portfolio domain what's in use.
 *
 * Halved on <=4 GB devices to keep this normalized mirror's heap footprint down; the
 * value is read once at module init since `deviceMemory` is static per session.
 */
const MAX_RETAINED_EVENTS = isLowMemoryDevice() ? 150 : 300;

interface MarketEntitiesActions {
    /** Upsert a market from a REST/search hydration. */
    marketHydrated: (market: MarketEntity) => void;
    /**
     * Upsert an event from a REST hydration; also touches its LRU access order.
     * `protectedTickers` shields the rest of the current hydration batch (e.g. every
     * event in one `useHydrateEventsIntoStore` call) from eviction by this event —
     * without it, hydrating >`MAX_RETAINED_EVENTS` events in one pass can evict
     * earlier events in the same batch before the loop finishes.
     */
    eventHydrated: (event: EventEntity, protectedTickers?: ReadonlySet<string>) => void;
    /**
     * Bulk upsert of events + their markets in a SINGLE store write. Prefer this over looping
     * `eventHydrated`/`marketHydrated` per market: those each do their own `set`, and because
     * consumers read this store via `useSyncExternalStore` (which re-renders synchronously on
     * every `set`), hydrating a many-market event one market at a time turns one pass into dozens
     * of nested synchronous re-renders — which trips React's "Maximum update depth exceeded" on
     * high-market surfaces like the home slideshow. Every event in the batch protects the others
     * from LRU eviction, exactly like `eventHydrated`'s `protectedTickers`.
     */
    eventsHydrated: (payloads: ReadonlyArray<{ event: EventEntity; markets: readonly MarketEntity[] }>) => void;
    /** Upsert a series from a REST hydration. */
    seriesHydrated: (series: SeriesEntity) => void;
    /** Bump an event's LRU access order (call on every render-driving read, not just hydration). */
    eventTouched: (ticker: string) => void;
    /** Protect a mounted consumer's event and markets from LRU eviction; returns an idempotent release. */
    eventRetained: (ticker: string) => () => void;
    /** Evict least-recently-accessed events past the cap, skipping retained events and `protectedTickers`. */
    staleEventsEvicted: (protectedTickers?: ReadonlySet<string>) => void;
    reset: () => void;
}

interface MarketEntitiesState {
    markets: Record<string, MarketEntity>;
    events: Record<string, EventEntity>;
    series: Record<string, SeriesEntity>;
    eventAccessOrder: Map<string, number>;
    actions: MarketEntitiesActions;
}

type GetFn = () => MarketEntitiesState;

// Module-level `get` so the non-hook `resolveEventTickerForMarketId` below can read the store.
let storeGet: GetFn;
let eventAccessCounter = 0;

/**
 * Reference-preserving upsert. A REST poll re-transforms EVERY entity of an event into fresh
 * objects even when only one field of one market moved, so most hydrations arriving here are
 * fresh-but-identical. `replaceEqualDeep` hands back the EXISTING entity when the incoming one is
 * deep-equal — which is what lets `MarketTiles.sameVisibleMarkets`, `BinaryMarketTile.arePropsEqual`,
 * and every `useShallow` selector over this store short-circuit, instead of re-rendering every
 * mounted consumer once per poll. When something did change, it structurally shares the unchanged
 * sub-objects (e.g. `rulebookVariables` keeps its reference when only prices moved). Entities are
 * plain data by construction (`marketEntityTransform`), which is what makes deep equality sound.
 */
const preservingReference = <T>(existing: T | undefined, incoming: T): T => replaceEqualDeep(existing, incoming);

/**
 * Plain listener `Set` (mirrors `userPositionsStore.subscribeToRawPosition`) so `queries/markets.ts`
 * can react to metadata changes without this store importing back into `queries/*` — `queries/markets.ts`
 * already imports `useMarketEntitiesStore`, so the reverse import would be circular.
 *
 * CURRENTLY WRITE-ONLY: nothing notifies these listeners. This store holds no WS subscription
 * anymore, so when the `market_metadata` channel ships on the gateway, its reducer belongs in
 * `tickerQuotesStore` (the WS owner) — which should then call a notify function exported from
 * here. The subscribe side is kept because `queries/markets.ts` is already wired to it and the
 * circular-import constraint above doesn't change.
 */
const metadataChangeListeners = new Set<(eventTicker: string) => void>();
export function subscribeToMarketMetadataChange(listener: (eventTicker: string) => void): () => void {
    metadataChangeListeners.add(listener);
    return () => metadataChangeListeners.delete(listener);
}

/**
 * Resolves a market's parent event ticker by market `id` (used by WS order
 * routing in `tradingSync.ts`/`userOrdersStore.ts`). Returns `''` when the market isn't
 * hydrated anywhere yet, matching the legacy fallback semantics: the previous lookup
 * always returned SOME market object, creating a blank stub whose parent event ticker
 * was `''` when nothing was resolvable — never `undefined`. A plain O(n) scan, not a
 * maintained index: this store is capped at `MAX_RETAINED_EVENTS`, and callers only
 * invoke this a handful of times per WS batch, not on a hot render path.
 */
export function resolveEventTickerForMarketId(marketId: string): string {
    const { markets, events } = storeGet();
    const market = Object.values(markets).find((m) => m.id === marketId);
    if (!market) return '';
    for (const event of Object.values(events)) {
        if (event.marketTickers.includes(market.ticker)) return event.ticker;
    }
    return '';
}

export const useMarketEntitiesStore = create<MarketEntitiesState>()((set, get) => {
    storeGet = get;
    // Each effect owns a distinct token, so overlapping consumers and Strict Mode cleanup
    // cannot release each other's retention. No React state changes for lease bookkeeping.
    const retainedEvents = new Map<string, Set<symbol>>();

    return {
        markets: {},
        events: {},
        series: {},
        eventAccessOrder: new Map(),
        actions: {
            marketHydrated: (market) =>
                set((state) => {
                    const next = preservingReference(state.markets[market.ticker], market);
                    // Same-reference result = content-equal re-hydration. Returning `state`
                    // makes the whole `set` a no-op (zustand skips notify on identity), so a
                    // redundant poll doesn't wake a single subscriber.
                    if (next === state.markets[market.ticker]) return state;
                    return { markets: { ...state.markets, [market.ticker]: next } };
                }),

            eventHydrated: (event, protectedTickers) => {
                set((state) => {
                    const next = preservingReference(state.events[event.ticker], event);
                    if (next === state.events[event.ticker]) return state;
                    return { events: { ...state.events, [event.ticker]: next } };
                });
                get().actions.eventTouched(event.ticker);
                // Never evicts the event just hydrated — it was just touched above, so
                // it's the most-recently-used entry and can't be an eviction candidate.
                get().actions.staleEventsEvicted(protectedTickers);
            },

            eventsHydrated: (payloads) => {
                if (payloads.length === 0) return;
                const protectedTickers = new Set(payloads.map((p) => p.event.ticker));
                // Touch LRU order for every hydrated event (mirrors `eventTouched`, which mutates
                // this module-level Map outside the `set` below).
                for (const { event } of payloads) get().eventAccessOrder.set(event.ticker, ++eventAccessCounter);
                set((state) => {
                    let changed = false;
                    const events = { ...state.events };
                    const markets = { ...state.markets };
                    for (const { event, markets: eventMarkets } of payloads) {
                        const nextEvent = preservingReference(events[event.ticker], event);
                        if (nextEvent !== events[event.ticker]) {
                            events[event.ticker] = nextEvent;
                            changed = true;
                        }
                        for (const market of eventMarkets) {
                            const nextMarket = preservingReference(markets[market.ticker], market);
                            if (nextMarket !== markets[market.ticker]) {
                                markets[market.ticker] = nextMarket;
                                changed = true;
                            }
                        }
                    }
                    // Same LRU eviction as `staleEventsEvicted`, applied once for the whole batch.
                    const overflow = Object.keys(events).length - MAX_RETAINED_EVENTS;
                    if (overflow > 0) {
                        const evictable = Object.keys(events)
                            .filter((ticker) => !protectedTickers.has(ticker) && !retainedEvents.has(ticker))
                            .sort((a, b) => (state.eventAccessOrder.get(a) ?? 0) - (state.eventAccessOrder.get(b) ?? 0))
                            .slice(0, overflow);
                        for (const ticker of evictable) {
                            for (const marketTicker of events[ticker]?.marketTickers ?? [])
                                delete markets[marketTicker];
                            delete events[ticker];
                            state.eventAccessOrder.delete(ticker);
                            changed = true;
                        }
                    }
                    // Every incoming entity was content-equal and nothing evicted: keep the
                    // records' identities so no subscriber is notified for a redundant poll.
                    if (!changed) return state;
                    return { events, markets };
                });
            },

            seriesHydrated: (series) =>
                set((state) => {
                    const next = preservingReference(state.series[series.ticker], series);
                    if (next === state.series[series.ticker]) return state;
                    return { series: { ...state.series, [series.ticker]: next } };
                }),

            eventTouched: (ticker) => {
                get().eventAccessOrder.set(ticker, ++eventAccessCounter);
            },

            eventRetained: (ticker) => {
                const token = Symbol();
                const consumers = retainedEvents.get(ticker) ?? new Set<symbol>();
                consumers.add(token);
                retainedEvents.set(ticker, consumers);
                return () => {
                    const current = retainedEvents.get(ticker);
                    if (!current?.delete(token)) return;
                    if (current.size === 0) retainedEvents.delete(ticker);
                    get().actions.staleEventsEvicted();
                };
            },

            staleEventsEvicted: (protectedTickers = new Set()) =>
                set((state) => {
                    const tickers = Object.keys(state.events);
                    const overflow = tickers.length - MAX_RETAINED_EVENTS;
                    if (overflow <= 0) return state;

                    const evictable = tickers
                        .filter((ticker) => !protectedTickers.has(ticker) && !retainedEvents.has(ticker))
                        .sort((a, b) => (state.eventAccessOrder.get(a) ?? 0) - (state.eventAccessOrder.get(b) ?? 0))
                        .slice(0, overflow);

                    if (evictable.length === 0) return state;

                    const events = { ...state.events };
                    const markets = { ...state.markets };
                    for (const ticker of evictable) {
                        // Evict the event's own markets too — otherwise `state.markets` (the
                        // largest part of this cache) never shrinks and stale markets stay
                        // discoverable by id/ticker scans indefinitely.
                        for (const marketTicker of events[ticker]?.marketTickers ?? []) {
                            delete markets[marketTicker];
                        }
                        delete events[ticker];
                        state.eventAccessOrder.delete(ticker);
                    }
                    return { events, markets };
                }),

            reset: () => {
                retainedEvents.clear();
                get().eventAccessOrder.clear();
                eventAccessCounter = 0;
                set({ markets: {}, events: {}, series: {} });
            },
        },
    };
});
